import React, {useState} from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Box, Collapse, IconButton} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {CampaignStatsMetadata, SocialStreamTab} from '@/types/socialMediaPost';
import StatCard from '@/components/stat-card';
import styles from './CampaignStatsSummary.module.scss';
import {
  getAverageStatsForStream,
  getTotalStatsForStream,
} from './campaignStatsConfig';

interface CampaignStatsSummaryProps {
  testID?: string;
  stats: CampaignStatsMetadata | undefined;
  socialStream: SocialStreamTab;
  network?: string | null;
  isLoading?: boolean;
  defaultExpanded?: boolean;
  dateRangeLabel?: string;
}

function CampaignStatsSummary({
  testID = 'campaign-stats-summary',
  stats,
  socialStream,
  network,
  isLoading = false,
  defaultExpanded = false,
  dateRangeLabel,
}: CampaignStatsSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const totals = stats?.totalsByNetwork;
  const averages = stats?.averagesByNetwork;

  const totalStats = getTotalStatsForStream(socialStream, network);
  const averageStats = getAverageStatsForStream(socialStream, network);

  if (!isLoading && !totals && !averages) {
    return null;
  }

  return (
    <Box className={styles.container} data-testid={testID}>
      <Box className={styles.collapsibleHeader}>
        <Box
          className={`${styles.headerContent}${dateRangeLabel ? ` ${styles.withDateRange}` : ''}`}
        >
          <Headline size="xl" className={styles.headerTitle}>
            {translate('Overview')}
          </Headline>
          {dateRangeLabel && (
            <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
              {dateRangeLabel}
            </Body>
          )}
        </Box>

        <IconButton
          size="small"
          className={styles.expandButton}
          data-testid={`${testID}-expand-button`}
          aria-label={isExpanded ? 'Collapse stats' : 'Expand stats'}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <ExpandMoreIcon
            className={`${styles.expandIcon} ${isExpanded ? styles.expandIconRotated : ''}`}
          />
        </IconButton>
      </Box>

      <Collapse in={isExpanded} timeout={200}>
        <Box
          className={styles.expandedContent}
          style={{
            gridTemplateColumns: `repeat(${totalStats.length + averageStats.length}, 1fr)`,
          }}
        >
          <Body
            size="md"
            color={designSystemToken('semantic.fg.inverse-secondary')}
            className={styles.sectionLabel}
            style={{gridColumn: `1 / span ${totalStats.length}`}}
          >
            {translate('Followers & Engagement')}
          </Body>
          <Body
            size="md"
            color={designSystemToken('semantic.fg.inverse-secondary')}
            className={styles.sectionLabel}
            style={{
              gridColumn: `${totalStats.length + 1} / span ${averageStats.length}`,
            }}
          >
            {translate('Average Performance')}
          </Body>

          {totalStats.map((stat, index) => (
            <StatCard
              key={stat.key}
              testID={`${testID}-total-${stat.key}`}
              iconName={stat.iconName}
              label={stat.label}
              value={totals ? stat.getValue(totals) : undefined}
              format={stat.format}
              isLoading={isLoading}
              highlighted={index === 0}
            />
          ))}
          {averageStats.map((stat, index) => (
            <StatCard
              key={stat.key}
              testID={`${testID}-average-${stat.key}`}
              iconName={stat.iconName}
              label={stat.label}
              value={averages ? stat.getValue(averages) : undefined}
              format={stat.format}
              isLoading={isLoading}
              highlighted={index === 0}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default CampaignStatsSummary;
